import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';

import ScheduleTask from '@totem/components/ticketing/details/tasks/scheduleTask';
import MyTicketsContext from '@totem/components/ticketing/myTickets/myTicketsContext';
import {
  MyTask,
  MyTaskExtended,
  MyTasks,
  Task,
  TicketingFilterOptions,
  TicketQueryResults,
} from '@totem/components/ticketing/ticketing';
import { getDefaultTaskWorkflowSteps } from '@totem/components/ticketing/utilities';
import InputContext from '@totem/components/ui_components/reportSettings/InputContext';
import { FilterField } from '@totem/components/ui_components/reportSettings/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ObjectReference } from '@totem/types/common';
import { isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT, TICKETS_ENDPOINT } from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { getPriorityFilter, getStatusFilter } from '@totem/utilities/ticketing';

export const containerConfiguration: FilterField[] = [
  {
    key: 'type',
    label: 'Ticket Type',
    sortField: 'number',
    filterField: 'type',
    multiple: true,
    values: [],
  },
  {
    key: 'category',
    label: 'Category',
    sortField: 'category',
    filterField: 'category',
    multiple: true,
    values: [],
  },
  {
    key: 'subCategory',
    label: 'Subcategory',
    sortField: 'subCategory',
    filterField: 'subCategory',
    multiple: true,
    values: [],
  },
  {
    key: 'organization',
    label: 'Organization',
    sortField: 'organization.name',
    filterField: 'organizationId',
    multiple: true,
    values: [],
  },
  {
    key: 'building',
    label: 'Building',
    sortField: 'building.name',
    filterField: 'buildingId',
    multiple: true,
    values: [],
  },
  {
    key: 'summary',
    label: 'Summary',
    sortField: 'summary',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'priority',
    label: 'Priority',
    sortField: 'priority',
    filterField: 'priority',
    multiple: true,
    values: [],
  },
  {
    key: 'impact',
    label: 'Impact',
    sortField: 'impact',
    filterField: 'impact',
    multiple: true,
    values: [],
  },
  {
    key: 'urgency',
    label: 'Urgency',
    sortField: 'urgency',
    filterField: 'urgency',
    multiple: true,
    values: [],
  },
  {
    key: 'status',
    label: 'Status',
    sortField: 'status',
    filterField: 'status',
    multiple: true,
    values: [],
  },
  {
    key: 'createdAt',
    label: 'Created Time',
    sortField: 'createdAt',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'updatedAt',
    label: 'Updated Time',
    sortField: 'updatedAt',
    filterField: '',
    multiple: true,
    values: [],
  },
  {
    key: 'reference',
    label: 'Reference',
    sortField: '',
    filterField: 'keys',
    multiple: true,
    values: [],
  },
];

type Props = {
  children?: React.ReactNode;
  componentKey?: string;
  onSetTotalRecords?: (recordCount: number) => void;
};

const MyTicketsContainer = ({ children, onSetTotalRecords }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTaskSending, setIsTaskSending] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(true);
  const [refreshMyTasks, setRefreshMyTasks] = useState<boolean>(true);
  const [action, setAction] = useState<string>('');
  const [actionExtras, setActionExtras] = useState<string>('');
  const [myTasks, setMyTasks] = useState<MyTasks>([]);
  const [myTasksExtended, setMyTasksExtended] = useState<MyTaskExtended[]>([]);
  const [selectedTask, setSelectedTask] = useState<MyTaskExtended>(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState<boolean>(false);
  const [ticketData, setTicketData] = useState<TicketQueryResults>(null);
  const [affiliatedQueues, setAffiliatedQueues] = useState<ObjectReference[]>(
    [],
  );
  const fetchAbortController = useRef<AbortController | null>(null);

  const {
    buildParameters,
    setFilterOptions,
    filterUpdateCount,
    sortingUpdateCount,
    pagingUpdateCount,
    isReady,
  } = useContext(InputContext);

  useEffect(() => {
    if (isReady) {
      setRefresh(true);
    }
  }, [
    isReady,
    filterUpdateCount,
    pagingUpdateCount,
    sortingUpdateCount,
    userProfile.organization.id,
  ]);

  // Get Filter Options
  useEffect(() => {
    fetch(`${TICKETS_ENDPOINT}/affiliated/filterOptions`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: TicketingFilterOptions) => {
        if (isNotNull(result)) {
          if (isNotNull(result.type)) {
            setFilterOptions(
              'type',
              result.type.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.impact)) {
            setFilterOptions(
              'impact',
              result.impact.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.urgency)) {
            setFilterOptions(
              'urgency',
              result.urgency.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          setFilterOptions('priority', getPriorityFilter());

          setFilterOptions('status', getStatusFilter());

          if (isNotNull(result.subStatus)) {
            setFilterOptions(
              'subStatus',
              result.subStatus.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.categories)) {
            setFilterOptions(
              'category',
              result.categories.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.building)) {
            setFilterOptions(
              'building',
              result.building.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.organization)) {
            setFilterOptions(
              'organization',
              result.organization.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }

          if (isNotNull(result.device)) {
            setFilterOptions(
              'device',
              result.device.map((opt) => {
                return { text: opt.name, value: opt.value };
              }),
            );
          }
        }
      });
  }, [getApiToken]);

  // Get Affiliated Queues
  useEffect(() => {
    fetch(`${TICKETS_ENDPOINT}/affiliated/queues`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: ObjectReference[]) => {
        setAffiliatedQueues(result);
      });
  }, [getApiToken]);

  // Get Affiliated Tickets
  useEffect(() => {
    if (!refresh) {
      return;
    }
    fetchAbortController.current?.abort();

    const controller = new AbortController();

    fetchAbortController.current = controller;

    setRefresh(false);

    setIsLoading(true);
    const params = buildParameters();
    fetch(`${TICKETS_ENDPOINT}/affiliated`, {
      method: 'POST',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
      body: JSON.stringify(params),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: TicketQueryResults) => {
        setTicketData(result);
        if (isNotNull(onSetTotalRecords)) {
          onSetTotalRecords(result.paging.totalRecords);
        }
      })
      .then(() => {
        setIsLoading(false);
      });
  }, [refresh, getApiToken]);

  // Get My Tasks & Set My Tasks / My Tasks Extended
  useEffect(() => {
    if (!refreshMyTasks) {
      return;
    }
    setRefreshMyTasks(false);

    fetch(`${TICKETS_ENDPOINT}/myTasks`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          return null;
        }
        return res.json();
      })
      .then((result: MyTasks) => {
        if (isNotNull(result)) {
          setMyTasks(result);
          const tasksExtended: MyTaskExtended[] = [];
          result.forEach((item: MyTask) => {
            if (isNotNull(item)) {
              item.tasks.forEach((task) => {
                tasksExtended.push({ ...task, ticket: item.ticket });
              });
            }
          });

          setMyTasksExtended(tasksExtended);
        }
      });
  }, [refreshMyTasks, getApiToken]);

  const sendTask = useCallback(
    async (paramTicketId: string, payload: Task) => {
      if (!isTaskSending) {
        fetch(`${TICKET_ENDPOINT}/${paramTicketId}/tasks`, {
          method: 'POST',
          headers: new Headers({
            Authorization: `Bearer ${getApiToken()}`,
          }),
          body: JSON.stringify(payload),
        }).then((res) => {
          setIsTaskSending(false);
          const success = CheckResponseShowError(res);
          if (success) {
            setRefreshMyTasks(true);
          }
        });
      }
    }, // End async
    [],
  );

  const handleStatusChange = (newStatus: string, refTask: MyTaskExtended) => {
    switch (newStatus) {
      case 'SCHEDULED':
        setSelectedTask(refTask);

        setShowScheduleDialog(true);
        break;
      case 'IN PROGRESS':
        {
          const startDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualStartDate: startDate,
          };
          sendTask(refTask.ticket.id, payload);
        }
        break;
      case 'COMPLETED':
        {
          const endDate = dayjs().toISOString();
          const payload = {
            ...refTask,
            status: newStatus,
            actualEndDate: endDate,
          };
          sendTask(refTask.ticket.id, payload);
        }
        break;
      default:
        {
          const payload = { ...refTask, status: newStatus };
          sendTask(refTask.ticket.id, payload);
        }
        break;
    }
  };

  const handleAction = (newAction: string, newActionExtras: string) => {
    switch (newAction) {
      case 'refresh':
        setRefresh(true);
        break;
      default:
        setAction(newAction);

        setActionExtras(newActionExtras);
    }
  };

  return (
    <MyTicketsContext.Provider
      value={{
        loading: isLoading,
        action,
        actionExtras,
        onAction: handleAction,
        myTasks,
        myTasksExtended,
        taskWorkflow: getDefaultTaskWorkflowSteps(),
        onTaskStatusChange: handleStatusChange,
        myTicketData: ticketData,
        myQueues: affiliatedQueues,
      }}
    >
      {children}
      {showScheduleDialog && (
        <ScheduleTask
          visible={showScheduleDialog}
          task={selectedTask}
          onCancel={() => {
            setShowScheduleDialog(false);
          }}
          onComplete={(newStartDate) => {
            setShowScheduleDialog(false);
            const payload = {
              ...selectedTask,
              status: 'SCHEDULED',
              expectedStartDate: newStartDate,
            };
            sendTask(selectedTask.ticket.id, payload);
          }}
        />
      )}
    </MyTicketsContext.Provider>
  );
};

export default MyTicketsContainer;
